import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import {
  type DatabaseTable,
  dbCan,
  type ExportScope,
  identityStyle,
  recordRecentDatabase,
  useDatabase,
  useTables,
} from '@/entities/database';
import { useDatabaseRealtime } from '@/entities/realtime';
import { useViewer } from '@/entities/viewer';
import { useWorkspaces } from '@/entities/workspace';
import {
  DatabaseAccessDialog,
  DatabaseSettingsDialog,
  DatabaseTitle,
} from '@/features/database-admin';
import { duplicatedMessage, DuplicateDatabaseDialog } from '@/features/database-catalog';
import { HistoryDialog } from '@/features/database-history';
import {
  ExportSnapshotDialog,
  exportRights,
  ImportTableDialog,
} from '@/features/database-portability';
import { TableTabs } from '@/features/database-tables';
import { BackLink, type BackTarget } from '@/features/resource-nav';
import { isApiError } from '@/shared/api';
import { originStateFrom } from '@/shared/lib/origin';
import { useIsMobile } from '@/shared/lib/media';
import {
  Button,
  EmptyState,
  ErrorState,
  IconButton,
  Icons,
  LoadingState,
  MenuItem,
  MenuList,
  MenuRow,
  Popover,
  useToast,
} from '@/shared/ui';

import styles from './DatabasePage.module.css';

// The grid, Markdown and the column / record dialogs load only when a table is opened.
const TableView = lazy(() =>
  import('@/features/database-grid').then((module) => ({ default: module.TableView })),
);

/**
 * A database: its title and database-level entry points (access, settings) above the table
 * tabs, the active table's data below. `/app/databases/:databaseId/:tableId?` - without a
 * table id the first table opens. Database settings are dialogs, never mixed into the grid.
 */
export function DatabasePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const { databaseId = '', tableId } = useParams();
  const database = useDatabase(databaseId);
  const workspaces = useWorkspaces();
  // Live changes of this database while its page is open (the server allows it for viewers only).
  useDatabaseRealtime(databaseId);
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const visible = database.data?.content_visible ?? false;
  // Only a database whose contents loaded (so the viewer may see it) counts as "recently opened".
  useEffect(() => {
    if (viewerId && visible && databaseId) recordRecentDatabase(viewerId, databaseId);
  }, [viewerId, visible, databaseId]);
  const tables = useTables(databaseId, visible);
  const [dialog, setDialog] = useState<
    'access' | 'settings' | 'duplicate' | 'export' | 'import' | 'history' | null
  >(null);
  const [exporting, setExporting] = useState<DatabaseTable | null>(null);
  const [scope, setScope] = useState<ExportScope | null>(null);

  // A database that was open and is now gone for this viewer (access removed, deleted): say so
  // once; the page itself then shows the same "not found" as for any stranger.
  const hadAccess = useRef(false);
  const lost = database.isError && isApiError(database.error) && database.error.status === 404;
  useEffect(() => {
    if (database.data) hadAccess.current = true;
    if (lost && hadAccess.current) {
      hadAccess.current = false;
      toast.show({ message: t('databases.sync.accessLost') });
    }
  }, [database.data, lost, toast, t]);

  const list = tables.data ?? [];
  const activeId = list.some((x) => x.id === tableId) ? (tableId ?? null) : (list[0]?.id ?? null);
  const base = `/app/databases/${databaseId}`;
  useEffect(() => {
    // An unknown / deleted table id falls back to the first table (replace: no history entry).
    if (tables.isSuccess && tableId && activeId !== tableId) {
      void navigate(activeId ? `${base}/${activeId}` : base, {
        replace: true,
        state: originStateFrom(location),
      });
    }
  }, [tables.isSuccess, tableId, activeId, base, navigate, location]);

  if (database.isPending) return <LoadingState />;
  if (database.isError) {
    const gone = isApiError(database.error) && database.error.status === 404;
    return (
      <div className={styles.page}>
        {gone ? (
          <EmptyState title={t('databases.notFound')}>
            <Link to="/app">{t('common.backHome')}</Link>
          </EmptyState>
        ) : (
          <ErrorState error={database.error} onRetry={() => void database.refetch()} />
        )}
      </div>
    );
  }
  const db = database.data;
  const canStructure = dbCan(db, 'database.manage_structure');
  const canManage = dbCan(db, 'database.manage_access');
  const rights = exportRights(db);

  const open = (name: NonNullable<typeof dialog>) => () => {
    setDialog(name);
  };
  // Direct link: the containing workspace when the viewer still sees it, else the Home catalog.
  const workspaceKnown = workspaces.data?.some((item) => item.id === db.workspace_id) ?? false;
  const fallback: BackTarget | null = workspaces.isSuccess
    ? workspaceKnown
      ? {
          to: `/app/workspaces/${db.workspace_id}?section=databases`,
          label: t('databases.backToWorkspace'),
          ariaLabel: t('databases.backToWorkspace'),
        }
      : { to: '/app', label: t('nav.home') }
    : null;
  const lock = db.is_confidential && (
    <span className={styles.lockBadge}>
      <Icons.lock />{' '}
      <span className={isMobile ? styles.srOnly : undefined}>{t('databases.confidential')}</span>
    </span>
  );
  const accessButton =
    canManage &&
    (isMobile ? (
      <IconButton
        label={t('databases.access.open')}
        icon={<Icons.users />}
        onClick={open('access')}
      />
    ) : (
      <Button variant="secondary" onClick={open('access')}>
        <Icons.users /> {t('databases.access.open')}
      </Button>
    ));
  // History: contents for those who may view them, access / export entries for access admins.
  const historyButton =
    (visible || canManage) &&
    (isMobile ? (
      <IconButton
        label={t('databases.history.open')}
        icon={<Icons.history />}
        onClick={open('history')}
      />
    ) : (
      <Button variant="secondary" onClick={open('history')}>
        <Icons.history /> {t('databases.history.open')}
      </Button>
    ));
  const exportButton = visible && rights.structure && (
    <IconButton
      label={t('databases.portability.export.action')}
      icon={<Icons.export />}
      onClick={open('export')}
    />
  );
  const duplicateButton = visible && (
    <IconButton
      label={t('databases.duplicate.action')}
      icon={<Icons.copy />}
      onClick={open('duplicate')}
    />
  );

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label={t('databases.breadcrumb')}>
        <BackLink fallback={fallback} />
      </nav>
      <header
        className={styles.header}
        data-testid="database-header"
        data-color={db.color ?? undefined}
        style={identityStyle(db.color, '--database-color')}
      >
        <div className={styles.titles}>
          <DatabaseTitle database={db} />
          {isMobile ? (
            lock
          ) : (
            <p className={styles.meta}>
              {lock}
              <span>{t('databases.ownerName', { name: db.owner_name })}</span>
              {db.my_role && (
                <span>{t('databases.myRole', { role: t(`roles.${db.my_role}`) })}</span>
              )}
            </p>
          )}
          {db.description && <p className={styles.description}>{db.description}</p>}
        </div>
        <div className={styles.actions} role="group" aria-label={t('databases.databaseActions')}>
          {accessButton}
          {historyButton}
          {isMobile ? (
            <Popover
              label={t('databases.databaseActions')}
              width={260}
              alignEnd
              trigger={(props, isOpen) => (
                <IconButton
                  {...props}
                  label={t('databases.databaseActions')}
                  icon={<Icons.more />}
                  {...(isOpen && { 'data-active': 'true' })}
                />
              )}
            >
              {(close) => (
                <MenuList>
                  {visible && rights.structure && (
                    <MenuRow>
                      <MenuItem
                        icon={<Icons.export />}
                        onClick={() => {
                          close();
                          setDialog('export');
                        }}
                      >
                        {t('databases.portability.export.action')}
                      </MenuItem>
                    </MenuRow>
                  )}
                  {visible && (
                    <MenuRow>
                      <MenuItem
                        icon={<Icons.copy />}
                        onClick={() => {
                          close();
                          setDialog('duplicate');
                        }}
                      >
                        {t('databases.duplicate.action')}
                      </MenuItem>
                    </MenuRow>
                  )}
                  <MenuRow>
                    <MenuItem
                      icon={<Icons.settings />}
                      onClick={() => {
                        close();
                        setDialog('settings');
                      }}
                    >
                      {t('databases.settings.title')}
                    </MenuItem>
                  </MenuRow>
                </MenuList>
              )}
            </Popover>
          ) : (
            <>
              {exportButton}
              {duplicateButton}
              <IconButton
                label={t('databases.settings.title')}
                icon={<Icons.settings />}
                onClick={open('settings')}
              />
            </>
          )}
        </div>
      </header>

      {!visible ? (
        <EmptyState title={t('databases.accessOnlyTitle')}>
          <p>{t('databases.accessOnlyText')}</p>
        </EmptyState>
      ) : tables.isPending ? (
        <LoadingState />
      ) : tables.isError ? (
        <ErrorState error={tables.error} onRetry={() => void tables.refetch()} />
      ) : (
        <div className={styles.body}>
          <TableTabs
            databaseId={db.id}
            tables={list}
            activeId={activeId}
            canStructure={canStructure}
            {...(rights.structure && { onExportTable: setExporting })}
            onImportTable={() => {
              setDialog('import');
            }}
            onSelect={(id) => {
              void navigate(`${base}/${id}`, { state: originStateFrom(location) });
            }}
          />
          {activeId ? (
            <Suspense fallback={<LoadingState />}>
              <TableView key={activeId} database={db} tableId={activeId} onScope={setScope} />
            </Suspense>
          ) : (
            <EmptyState title={t('databases.noTables')}>
              <p>{canStructure ? t('databases.noTablesBuilder') : t('databases.noTablesReader')}</p>
            </EmptyState>
          )}
        </div>
      )}

      {dialog === 'access' && (
        <DatabaseAccessDialog
          database={db}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'history' && (
        <HistoryDialog
          database={db}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'duplicate' && (
        <DuplicateDatabaseDialog
          source={db}
          onClose={() => {
            setDialog(null);
          }}
          onDuplicated={(copy, report) => {
            setDialog(null);
            toast.show({ message: duplicatedMessage(t, copy.name, report) });
            void navigate(`/app/databases/${copy.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {dialog === 'export' && (
        <ExportSnapshotDialog
          target={{ kind: 'database', id: db.id }}
          name={db.name}
          confidential={db.is_confidential}
          recordsAllowed={rights.records}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {exporting && (
        <ExportSnapshotDialog
          target={{ kind: 'table', id: exporting.id }}
          name={exporting.name}
          confidential={db.is_confidential}
          recordsAllowed={rights.records}
          scope={scope?.tableId === exporting.id ? scope : undefined}
          onClose={() => {
            setExporting(null);
          }}
        />
      )}
      {dialog === 'import' && (
        <ImportTableDialog
          database={db}
          onClose={() => {
            setDialog(null);
          }}
          onImported={(table, report) => {
            setDialog(null);
            toast.show({
              message: duplicatedMessage(t, table.name, report),
            });
            void navigate(`${base}/${table.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {dialog === 'settings' && (
        <DatabaseSettingsDialog
          database={db}
          onClose={() => {
            setDialog(null);
          }}
          onDeleted={() => {
            setDialog(null);
            void navigate(`/app/workspaces/${db.workspace_id}?section=databases`, {
              replace: true,
            });
          }}
        />
      )}
    </div>
  );
}
